import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { getTeamId } from "@/hooks/useTeamId";

export function useProfile() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const query = useQuery({
    queryKey: ["profile", user?.id],
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", user!.id)
        .single();
      if (error) throw error;
      return data;
    },
  });

  const mutation = useMutation({
    mutationFn: async (values: Record<string, unknown>) => {
      const { data: updated, error } = await supabase
        .from("profiles")
        .update(values)
        .eq("user_id", user!.id)
        .select()
        .single();
      if (error) throw error;
      return updated;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      toast({ title: "Alterações guardadas" });
    },
    onError: () => {
      toast({ title: "Erro ao guardar", variant: "destructive" });
    },
  });

  return { profile: query.data, isLoading: query.isLoading, updateProfile: mutation.mutate, isSaving: mutation.isPending };
}

function useSettingsTable<T>(table: string) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const query = useQuery({
    queryKey: [table, user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from(table as any)
        .select("*")
        .eq("user_id", user!.id)
        .single();
      if (error && error.code === "PGRST116") {
        // No row found, create default with team_id
        const teamId = await getTeamId();
        const { data: created, error: insertError } = await supabase
          .from(table as any)
          .insert({ user_id: user!.id, team_id: teamId } as any)
          .select()
          .single();
        if (insertError) throw insertError;
        return created as T;
      }
      if (error) throw error;
      return data as T;
    },
  });

  const mutation = useMutation({
    mutationFn: async (values: Record<string, unknown>) => {
      const { data: updated, error } = await supabase
        .from(table as any)
        .update(values as any)
        .eq("user_id", user!.id)
        .select()
        .single();
      if (error) throw error;
      return updated;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [table] });
      toast({ title: "Alterações guardadas" });
    },
    onError: () => {
      toast({ title: "Erro ao guardar", variant: "destructive" });
    },
  });

  return { data: query.data, isLoading: query.isLoading, update: mutation.mutate, isSaving: mutation.isPending };
}

export function useCompany() {
  return useSettingsTable<any>("companies");
}

export function useFinancialSettings() {
  return useSettingsTable<any>("financial_settings");
}

export function useAutomationSettings() {
  return useSettingsTable<any>("automation_settings");
}

export function useNotificationSettings() {
  return useSettingsTable<any>("notification_settings");
}

export function useSubscription() {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: ["subscriptions", user?.id],
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("subscriptions")
        .select("*")
        .eq("user_id", user!.id)
        .single();
      if (error && error.code === "PGRST116") {
        const { data: created, error: insertError } = await supabase
          .from("subscriptions")
          .insert({ user_id: user!.id } as any)
          .select()
          .single();
        if (insertError) throw insertError;
        return created;
      }
      if (error) throw error;
      return data;
    },
  });

  // Read-only: subscription changes must go through server-side billing
  return { data: query.data, isLoading: query.isLoading };
}
